import {
  ANNOTATION_EDIT_URL,
  Entity,
  parseEntityRef,
} from '@backstage/catalog-model';
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  IconButton,
} from '@material-ui/core';
import {
  HeaderIconLinkRow,
  IconLinkVerticalProps,
  MarkdownContent,
  OverflowTooltip,
} from '@backstage/core-components';
import { catalogApiRef, entityRouteRef } from '@backstage/plugin-catalog-react';
import { useElementFilter, useRouteRef } from '@backstage/core-plugin-api';
import { ConfluenceIcon } from '@internal/ui-common';
import EditIcon from '@material-ui/icons/Edit';
import EmailIcon from '@material-ui/icons/Email';
import type { IMessage } from '../../../schemas/IMessage.interface';
import { ReactNode } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { useAsync } from 'react-use';
import { useStyles } from './aboutSection.style';

interface AboutSectionProps {
  message: IMessage;
}

interface AboutCardProps {
  title: string;
  subheader?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}

function AboutCard({
  title,
  subheader,
  action,
  children,
  className,
}: AboutCardProps) {
  return (
    <Card className={className}>
      <CardHeader title={title} subheader={subheader} action={action} />
      <Divider />
      <CardContent>{children}</CardContent>
    </Card>
  );
}

interface AboutFieldProps {
  label: string;
  value?: string;
  gridSizes?: {
    xs?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
    sm?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
    lg?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  };
  children?: ReactNode;
  bold?: boolean;
  className?: string;
}

function AboutField({
  label,
  value,
  gridSizes = { xs: 12, sm: 4 },
  children,
  bold = false,
  className,
}: AboutFieldProps) {
  const { cx, classes } = useStyles();

  const childElements = useElementFilter(children, child =>
    child.getElements(),
  );

  let content: ReactNode;
  if (childElements.length > 0) {
    content = bold ? (
      <p className={cx(classes.value)}>{childElements}</p>
    ) : (
      childElements
    );
  } else {
    content = <p className={cx(classes.value)}>{value || 'unknown'}</p>;
  }

  return (
    <Grid
      item
      xs={gridSizes.xs}
      sm={gridSizes.sm}
      lg={gridSizes.lg}
      className={className}
    >
      <h2 className={classes.label}>{label}</h2>
      {content}
    </Grid>
  );
}

function AboutSection({ message }: AboutSectionProps) {
  const { cx, classes } = useStyles();

  const catalogApi = useApi(catalogApiRef);
  const entityRoute = useRouteRef(entityRouteRef);

  const { value: entity } = useAsync(async (): Promise<Entity | undefined> => {
    if (!message.entityRef) return undefined;

    return await catalogApi.getEntityByRef(message.entityRef);
  }, [catalogApi, message.entityRef]);

  const ownerHref: string | undefined = (() => {
    const ownedByRelation = entity?.relations?.find(
      relation => relation.type === 'ownedBy',
    );
    const ownerRef = ownedByRelation?.targetRef;

    if (!ownerRef) return undefined;

    try {
      const { kind, namespace, name } = parseEntityRef(ownerRef);
      return entityRoute({ kind, namespace, name });
    } catch {
      return undefined;
    }
  })();

  const ingestionAppsText = formatIngestionApps(message.ingestionApps);
  const headerLinks = buildHeaderLinks(message.confluenceUrl, ownerHref);
  const editUrl = entity?.metadata?.annotations?.[ANNOTATION_EDIT_URL];

  const fields = [
    ...(message.name
      ? [
          {
            label: 'Type',
            bold: true,
            children: <OverflowTooltip text={message.name} />,
          },
        ]
      : []),
    ...(message.status ? [{ label: 'Status', value: message.status }] : []),
    ...(ingestionAppsText
      ? [{ label: 'Ingestion apps', value: ingestionAppsText }]
      : []),
  ];

  const action = editUrl ? (
    <IconButton
      size="small"
      aria-label="Edit specification in GitLab"
      component="a"
      href={editUrl}
      target="_blank"
      rel="noopener noreferrer"
    >
      <EditIcon />
    </IconButton>
  ) : undefined;

  const subheader = headerLinks.length ? (
    <HeaderIconLinkRow links={headerLinks} />
  ) : undefined;

  return (
    <AboutCard title="About" subheader={subheader} action={action}>
      <Grid container spacing={2} className={cx(classes.contentGrid)}>
        {message.description && (
          <AboutField label="Description" gridSizes={{ xs: 12 }}>
            <MarkdownContent content={message.description} />
          </AboutField>
        )}

        {fields.map((field, index) => (
          <AboutField key={index} {...field} />
        ))}
      </Grid>
    </AboutCard>
  );
}

function formatIngestionApps(apps: string[] | undefined): string | undefined {
  return apps?.length
    ? apps.map(app => app.toUpperCase()).join(', ')
    : undefined;
}

function buildHeaderLinks(
  confluenceUrl: IMessage['confluenceUrl'] | undefined,
  ownerHref: string | undefined,
): IconLinkVerticalProps[] {
  const links: IconLinkVerticalProps[] = [];

  if (confluenceUrl) {
    links.push({
      label: 'Confluence documentation',
      href: confluenceUrl,
      icon: <ConfluenceIcon size={18} />,
    });
  }

  if (ownerHref) {
    links.push({
      label: 'Contact owner',
      href: ownerHref,
      icon: <EmailIcon fontSize="small" />,
    });
  }

  return links;
}

export { AboutSection };
